import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export type ProductCategory = 'JERSEY' | 'SCARF' | 'HAT' | 'ACCESSORY' | 'OTHER';

export interface ProductSizeDto {
  size: string;
  stock: number;
}

export interface ProductDto {
  id: string;
  name: { it?: string; en?: string };
  description: { it?: string; en?: string };
  price: number;
  currency: string;
  images: string[];
  category: ProductCategory;
  sku: string;
  isAvailable: boolean;
  /** T-113 — ordine di visualizzazione nello shop (1 = in cima). */
  displayOrder: number;
  customizable: boolean;
  allowName: boolean;
  allowNumber: boolean;
  /** @deprecated T-40: non più usato per il calcolo del prezzo (ora fisso). */
  customizationPricePerChar: number;
  customizationMaxLength: number;
  sizes: ProductSizeDto[];
  // T-69 — tassonomia shop / campi catalogo estesi
  macrocategory: string | null;
  subcategory: string | null;
  sizeBand: string | null;
  productLine: string | null;
  sourceCode: string | null;
}

export interface ProductSizeInput {
  size: string;
  stock: number;
}

export interface CreateProductInput {
  name: { it?: string; en?: string };
  description: { it?: string; en?: string };
  price: number;
  currency?: string;
  images: string[];
  category: ProductCategory;
  sku: string;
  isAvailable?: boolean;
  customizable?: boolean;
  allowName?: boolean;
  allowNumber?: boolean;
  customizationMaxLength?: number;
  sizes?: ProductSizeInput[];
  // T-69 — tassonomia shop / campi catalogo estesi (tutti opzionali)
  macrocategory?: string | null;
  subcategory?: string | null;
  sizeBand?: string | null;
  productLine?: string | null;
}

export type UpdateProductInput = Partial<CreateProductInput>;

export interface PaginatedProducts {
  data: ProductDto[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

interface ProductsFilters {
  page?: number;
  limit?: number;
  category?: ProductCategory;
  search?: string;
  available?: boolean;
  // T-87 — navigazione shop macro → sotto (tassonomia Impostazioni Shop).
  macrocategory?: string;
  subcategory?: string;
}

const QK = {
  list: (filters: ProductsFilters) => ['products', filters] as const,
};

export function useProducts(filters: ProductsFilters = {}) {
  return useQuery({
    queryKey: QK.list(filters),
    queryFn: async () => {
      const params: Record<string, string | number | boolean> = {};
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.category) params.category = filters.category;
      if (filters.search) params.search = filters.search;
      if (filters.available !== undefined) params.available = filters.available;
      if (filters.macrocategory) params.macrocategory = filters.macrocategory;
      if (filters.subcategory) params.subcategory = filters.subcategory;
      const res = await apiClient.get<PaginatedProducts>('/shop/products', { params });
      return res.data;
    },
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateProductInput) => {
      const res = await apiClient.post<ProductDto>('/shop/products', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateProductInput }) => {
      const res = await apiClient.patch<ProductDto>(`/shop/products/${id}`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/shop/products/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

/**
 * T-113 — riordino in blocco. `order` è la lista COMPLETA degli id prodotto del
 * tenant nell'ordine desiderato (primo = posizione 1). Sia il drag-and-drop sia
 * la modifica inline del numero passano da qui ricostruendo l'array intero.
 */
export function useReorderProducts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (order: string[]) => {
      await apiClient.patch('/shop/products/reorder', { order });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}
